import React, {FC} from 'react';
import {Grid, Skeleton, Typography} from '@mui/material';
import {formatCurrencyValue} from "../../../util/PaymentUtils";
import {ICostBreakdown, ICostBreakdownItem, ICostBreakdownTotalItem} from "./interfaces";
import {styled} from "@mui/system";

/**
 * A component for displaying a single line item in a cost breakdown.
 * @param item {ICostBreakdownItem} The item to display
 * @deprecated Migrate to new SecurSpaceCostBreakdown composition components
 */
export const LineItem: FC<{ item: ICostBreakdownItem; loading?: boolean; }> = ({item, loading}) => {
  const createValueComponent = (item: ICostBreakdownItem) => {
    if (item.formattedValue === undefined || item.formattedValue === null) {
      return <Typography variant={'h6'}>{formatCurrencyValue(item.value, true)}</Typography>;
    }
    return React.isValidElement(item.formattedValue) ?
      item.formattedValue : <Typography variant={'h6'}>{item.formattedValue}</Typography>;
  };

  return (
    <Grid item container justifyContent={'space-between'} alignContent={'center'}>
      <Grid item>
        {
          React.isValidElement(item.label) ? item.label : <Typography variant={"h6"} color={"textSecondary"}>
            {item.label}
          </Typography>
        }
      </Grid>
      <Grid item>
        {
          loading ? <Typography variant={"h6"}><Skeleton variant={'text'} width={'8em'}/></Typography>
            : createValueComponent(item)
        }
      </Grid>
    </Grid>
  );
}

const TotalLineItem: FC<{ item: ICostBreakdownTotalItem; calculatedTotal: number; loading?: boolean }> = ({
                                                                                                            item,
                                                                                                            calculatedTotal,
                                                                                                            loading
                                                                                                          }) => {
    const createValueComponent = (item: ICostBreakdownTotalItem, calculatedTotal: number) => {
    if (item.formattedValue !== undefined && item.formattedValue !== null) {
      return React.isValidElement(item.formattedValue) ?
        item.formattedValue : <Typography variant={'h5'}>{item.formattedValue}</Typography>;
    }
    if (item.value !== undefined && item.value !== null) {
      return <Typography variant={'h5'}>{formatCurrencyValue(item.value, true)}</Typography>;
    }
    return <Typography variant={'h5'}>{formatCurrencyValue(calculatedTotal, true)}</Typography>;
  };
  return <>
    <Grid item>
      {React.isValidElement(item.label) ? item.label : <Typography variant={"h5"}>{item.label}</Typography>}
    </Grid>
    {
      loading ? <Typography variant={'h5'}><Skeleton variant={'text'} width={'8em'}/></Typography>
        : createValueComponent(item, calculatedTotal)
    }
  </>
};

const TotalContainer = styled(Grid)(() => ({
  marginTop: '1rem',
  borderTop: '1px solid #E0E0E0',
  paddingTop: '1rem'
}));

/**
 * A component for displaying an itemized breakdown of costs and a calculated total.
 * @param children {React.ReactNode} Additional content to display e.g. LineItems. Additional content values will not be included in the total. (TODO: Use refs to include additional content in the total)
 * @param className {string} The class name to apply to the root element
 * @param items {ICostBreakdownItem[]} The items to display in the cost breakdown
 * @param loading {boolean} Whether the component is in a loading state
 * @param total {ICostBreakdownTotalItem} The total line item to display at the bottom of the cost breakdown. The value or formattedValue field will replace the calculated total if provided.
 * @param includeTotal {ICostBreakdownTotalItem} Conditionally render the 'Total' line.
 * @param hideValue {boolean} Whether or not to hide the value of the line item
 * @constructor
 * @deprecated Migrate to new SecurSpaceCostBreakdown composition components
 */
const CostBreakdown: FC<ICostBreakdown> = ({
                                            children,
                                            className,
                                            items = [],
                                            loading = false,
                                            total = {label: 'Total'},
                                            includeTotal = true,
                                            hideValue = false
                                          }: ICostBreakdown) => {
    const calculatedTotal = total.value ? total.value : items.reduce((sum, item) => sum + item.value, 0);

    return (
      <Grid container className={className}>
        {
          items.map((item, index) => !hideValue && <LineItem item={item} key={index} loading={loading} />)
        }
          {children}
        {
          includeTotal ?
            <TotalContainer item container justifyContent={'space-between'} alignContent={'center'}>
              {
                !hideValue && <TotalLineItem item={total} calculatedTotal={calculatedTotal} loading={loading} />
              }
            </TotalContainer>
          : ""
        }
      </Grid>
    );
};

export default CostBreakdown;
